/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Link } from '@fluentui/react/lib/Link';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { AutoTaggingJobDetailsPanel } from 'src/customText/appShell/autoTaggingPage/components/AutoTaggingJobDetailsPanel';
import { AutoTaggingJobTable } from 'src/customText/appShell/autoTaggingPage/components/AutoTaggingJobTable';
import { AutoTaggingJobTableCommandBar } from 'src/customText/appShell/autoTaggingPage/components/AutoTaggingJobTableCommandBar';
import { AutoTaggingQuotaConsumptionCard } from 'src/customText/appShell/autoTaggingPage/components/AutoTaggingQuotaConsumptionCard';
import { AutotagTriggerModal } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModal } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModal';
import { AutoLabelingInfoDto, AutoTaggingJobItem } from 'src/customText/verticals/autotagTriggerModal/types';

const messages = defineMessages({
    classes: { id: 'customText.customTextAutoTaggingPage.classes', defaultMessage: 'classes' },
    preview: { id: 'customText.customTextAutoTaggingPage.preview', defaultMessage: 'Preview' },
    entities: { id: 'customText.customTextAutoTaggingPage.entities', defaultMessage: 'entities' },
    pageTitle: { id: 'customText.customTextAutoTaggingPage.pageTitle', defaultMessage: 'Auto-labeling' },
    dataLabeling: { id: 'customText.customTextAutoTaggingPage.dataLabeling', defaultMessage: 'Data labeling' },
    learnMore: { id: 'customText.customTextAutoTaggingPage.learnMore', defaultMessage: 'Learn more about auto-labeling.' },
    notification: { id: 'customText.customTextAutoTaggingPage.notification', defaultMessage: 'Training complete notification' },
    pageDescription: {
        id: 'customText.customTextAutoTaggingPage.pageDescription',
        defaultMessage:
            'After you have completed training a model, You can now trigger an auto-labeling job using the model that you have just trained.'
    },
    gptPageDescription1: {
        id: 'customText.customTextAutoTaggingPage.gptPageDescription1',
        defaultMessage: `Speed up the work of labeling your data by using a trained model or GPT suggest entity labels in new documents.
        start an auto-labeling job to select the documents and { models } to analyze. review and accept or reject the suggestions from successful
        jobs in`
    },
    gptPageDescription2: {
        id: 'customText.customTextAutoTaggingPage.gptPageDescription2',
        defaultMessage: `jobs from the last 7 days are displayed here.`
    },
    noTrainedModels: {
        id: 'customText.customTextAutoTaggingPage.noTrainedModels',
        defaultMessage: 'There are no trained models in your project. Please train a model in order to trigger an auto-labeling job.'
    },
    emptyStateMessage: {
        id: 'customText.customTextAutoTaggingPage.emptyStateMessage',
        defaultMessage: `You don't have any auto-labeling jobs yet. Train a model and then Select Trigger Auto-label to begin a new job.`
    }
});

export const CustomTextAutoTaggingPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const currentProject = rootStore.currentCustomTextProjectStore;
    const {
        projectKind,
        trainedModels,
        extractionStore,
        autoTaggingQuota,
        gptJobsStoreHouse,
        classificationStore,
        autoTaggingJobStoreHouse,
        currentRunningAutoTaggingJob
    } = currentProject;
    let autoTaggingJobs: AutoTaggingJobItem[] = autoTaggingJobStoreHouse.items;
    autoTaggingJobs = autoTaggingJobs.concat(gptJobsStoreHouse.items);

    const [query, setQuery] = React.useState('');
    const [isAutotagTriggerModalHidden, setIsAutotagTriggerModalHidden] = React.useState(true);
    const [sortKey, setSortKey] = React.useState<SortKeyType<AutoTaggingJobItem>>({ key: 'createdDateTime', order: 'desc' });
    const [autoTaggingJobDetailsPanelSettings, updateAutoTaggingJobDetailsPanelSettings] = useUpdateState<{
        isOpen: boolean;
        selectedJobId: string;
    }>({ isOpen: false, selectedJobId: '' });

    const currentRunningGptJob = gptJobsStoreHouse.items.find(j => j.status === 'inProgress' || j.status === 'notStarted');
    const hasNoTrainedModels = currentProject.trainingJobStoreHouse.tracker.isCompleted && !currentProject.successfullyTrainedModel.length;
    const isLoading = autoTaggingJobStoreHouse.tracker.isRunning || gptJobsStoreHouse.tracker.isRunning || trainedModels.tracker.isRunning;
    const isInEmptyState = !isLoading && !autoTaggingJobs.length;
    const isSelectedGptJob = gptJobsStoreHouse.findById(autoTaggingJobDetailsPanelSettings.selectedJobId);
    const isTriggerAutoTaggingJobDisabled =
        (!FeatureToggles.CustomTextGptLabeling && hasNoTrainedModels) ||
        !!currentRunningAutoTaggingJob ||
        !!currentRunningGptJob ||
        !!autoTaggingJobStoreHouse.items.find(j => j.status === 'notStarted') ||
        (projectKind === 'CustomEntityRecognition' && extractionStore.entities.length === 0) ||
        ((projectKind === 'CustomMultiLabelClassification' || projectKind === 'CustomSingleLabelClassification') &&
            classificationStore.classes.length === 0);
    const isReviewAutotagsDisabled =
        isInEmptyState ||
        (!!currentRunningAutoTaggingJob && !!currentRunningGptJob) ||
        (!autoTaggingJobStoreHouse.items.some(i => i.status === 'succeeded') &&
            !gptJobsStoreHouse.items.some(i => i.status === 'succeeded'));
    const learnMoreLink = projectKind === 'CustomEntityRecognition' ? 'https://go.microsoft.com/fwlink/?linkid=2256961' : 'https://go.microsoft.com/fwlink/?linkid=2257140';

    const filteredJobs = orderBy(
        substringSearchStrategy(autoTaggingJobs, query, ['id', 'trainedModelLabel']),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const triggerAutoTagJob = (autotagInfoDto: AutoLabelingInfoDto) => {
        dispatcher.dispatch('autoTagCustomTextModel', {
            trainedModelLabel: autotagInfoDto.trainedModelLabel,
            documents: autotagInfoDto.documents,
            extractors: autotagInfoDto.entitiesNames
        });

        setIsAutotagTriggerModalHidden(true);
    };

    const onModelClick = (modelId: string) => {
        history.push(routeTable.customText.modelDetailsPage(currentProject.id, currentProject.projectKind, modelId));
    };

    return (
        <Page pageName="CustomTextAutoTaggingPage" title={localize(messages.pageTitle)} reactToZoomLevel={false}>
            <Stack tokens={{ padding: 32, childrenGap: 12 }}>
                <Stack.Item>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                        <EditableTitle title={localize(messages.pageTitle)} />
                        <LuisText styles={{ root: { fontStyle: 'italic' } }}>{localize(messages.preview)}</LuisText>
                    </Stack>
                    <LuisText>
                        {!FeatureToggles.CustomTextGptLabeling
                            ? localize(messages.pageDescription)
                            : localize(messages.gptPageDescription1, {
                                  models:
                                      projectKind === 'CustomEntityRecognition' ? localize(messages.entities) : localize(messages.classes)
                              })}
                        {FeatureToggles.CustomTextGptLabeling && (
                            <>
                                &nbsp;
                                <Link
                                    styles={{ root: { textDecoration: 'underline' } }}
                                    onClick={() =>
                                        history.push(routeTable.customText.dataTaggingPage(currentProject.id, currentProject.projectKind))
                                    }>
                                    {localize(messages.dataLabeling)}
                                </Link>
                                &nbsp;{localize(messages.gptPageDescription2)}
                                &nbsp;<ExternalLink href={learnMoreLink}>{localize(messages.learnMore)}</ExternalLink>
                            </>
                        )}
                    </LuisText>
                </Stack.Item>

                <AutoTaggingJobTableCommandBar
                    query={query}
                    onQueryChange={setQuery}
                    isReviewAutotagsDisabled={isReviewAutotagsDisabled}
                    isTriggerAutoTaggingJobDisabled={isTriggerAutoTaggingJobDisabled}
                    onTriggerAutoTaggingJob={() => setIsAutotagTriggerModalHidden(false)}
                />

                {!FeatureToggles.CustomTextGptLabeling && hasNoTrainedModels && (
                    <MessageBar messageBarType={MessageBarType.severeWarning}>{localize(messages.noTrainedModels)}</MessageBar>
                )}

                {projectKind === 'CustomEntityRecognition' && (
                    <AutoTaggingQuotaConsumptionCard
                        totalMonthlyQuota={autoTaggingQuota?.totalQuota}
                        totalConsumedQuota={autoTaggingQuota?.consumedQuota}
                    />
                )}

                {isInEmptyState ? (
                    <EmptyStateView message={localize(messages.emptyStateMessage)} />
                ) : (
                    <AutoTaggingJobTable
                        sortKey={sortKey}
                        items={filteredJobs}
                        isLoading={isLoading}
                        onModelClick={onModelClick}
                        onAutoTaggingJobClick={selectedJobId => updateAutoTaggingJobDetailsPanelSettings({ isOpen: true, selectedJobId })}
                        onSortColumn={c =>
                            setSortKey({ key: c.fieldName as keyof AutoTaggingJobItem, order: c.isSortedDescending ? 'asc' : 'desc' })
                        }
                    />
                )}
            </Stack>
            <AutoTaggingJobDetailsPanel
                onModelClick={onModelClick}
                isGptJob={Boolean(isSelectedGptJob)}
                isOpen={autoTaggingJobDetailsPanelSettings.isOpen}
                onDismiss={() => updateAutoTaggingJobDetailsPanelSettings({ isOpen: false })}
                autoTaggingJob={isSelectedGptJob ?? autoTaggingJobStoreHouse.findById(autoTaggingJobDetailsPanelSettings.selectedJobId)}
            />
            {FeatureToggles.CustomTextGptLabeling ? (
                <AutoLabelingModal
                    hidden={isAutotagTriggerModalHidden}
                    onSubmit={() => setIsAutotagTriggerModalHidden(true)}
                    onDismiss={() => setIsAutotagTriggerModalHidden(true)}
                />
            ) : (
                <AutotagTriggerModal
                    onSubmit={triggerAutoTagJob}
                    hidden={isAutotagTriggerModalHidden}
                    onDismiss={() => setIsAutotagTriggerModalHidden(true)}
                />
            )}
        </Page>
    );
});
